import { useCallback, useMemo } from 'react'

import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { useSocialMediaType } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'
import { handleError } from '@/utils/error'

import {
  addSocialAccount,
  deleteSocialAccountByCreatorId,
} from '../data/service'
import type { SocialAccount } from '../types'

export const useUpdateSocialAccounts = () => {
  const queryClient = useQueryClient()
  const { socialMediaType } = useSocialMediaType()
  const creatorId = useUnit($creatorId)

  const deleteMutation = useMutation(
    (id: string) =>
      deleteSocialAccountByCreatorId(creatorId!, socialMediaType, id),
    {
      onMutate: (accountId) => {
        queryClient.setQueryData<SocialAccount[] | undefined>(
          ['social', creatorId, socialMediaType],
          (prevAccounts) => {
            return prevAccounts?.filter(
              (account) => String(account.id) !== accountId,
            )
          },
        )
      },
      onSettled: () => invalidateCache(),
      onError: (error) => handleError(error, 'Failed to delete social account'),
    },
  )

  const addMutation = useMutation(
    (username: string) =>
      addSocialAccount(creatorId!, socialMediaType, username),
    {
      onSettled: () => invalidateCache(),
      onError: (error) => handleError(error, 'Failed to add social account'),
    },
  )

  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries('social')
  }, [queryClient])

  return useMemo(
    () => ({
      deleteAccount: deleteMutation,
      addAccount: addMutation,
    }),
    [addMutation, deleteMutation],
  )
}
